import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { EmotionProjectReply } from "~/app/textToSpeech/project.service";

@Component({
  selector: "app-emotion-reply",
  templateUrl: "./emotion-reply.component.html",
  styleUrls: ["./emotion-reply.component.scss"],
})
export class EmotionReplyComponent implements OnChanges {
  private static readonly EMOTION_SUGGESTION_MAP: Record<string, string[]> = {
    angry: [
      "[angry]",
      "[furious]",
      "[enraged]",
      "[seething]",
      "[outraged]",
      "[irritated]",
      "[frustrated]",
      "[fuming]",
      "[bitter]",
      "[hostile]",
    ],
    disgusted: [
      "[disgusted]",
      "[revolted]",
      "[appalled]",
      "[repulsed]",
      "[contemptuous]",
      "[disdainful]",
      "[nauseated]",
      "[scornful]",
      "[with disgust]",
      "[grimacing]",
    ],
    fearful: [
      "[fearful]",
      "[terrified]",
      "[panicked]",
      "[nervously]",
      "[anxiously]",
      "[trembling]",
      "[petrified]",
      "[dreading]",
      "[with dread]",
      "[in a shaking voice]",
    ],
    happy: [
      "[happy]",
      "[joyful]",
      "[cheerfully]",
      "[excited]",
      "[delighted]",
      "[elated]",
      "[gleefully]",
      "[playfully]",
      "[enthusiastically]",
      "[warmly]",
    ],
    neutral: [
      "[neutral]",
      "[flatly]",
      "[calmly]",
      "[deadpan]",
      "[matter-of-factly]",
      "[evenly]",
      "[composed]",
      "[indifferent]",
      "[monotone]",
      "[stoic]",
    ],
    sad: [
      "[sad]",
      "[sorrowful]",
      "[tearfully]",
      "[mournful]",
      "[dejected]",
      "[heartbroken]",
      "[melancholic]",
      "[despondent]",
      "[forlorn]",
      "[quietly]",
    ],
    surprised: [
      "[surprised]",
      "[shocked]",
      "[astonished]",
      "[gasps]",
      "[stunned]",
      "[wide-eyed]",
      "[amazed]",
      "[bewildered]",
      "[startled]",
      "[taken aback]",
    ],
  };

  private static readonly EMOTION_CANONICAL_MAP: Record<string, string> = {
    anger: "angry",
    angry: "angry",
    furious: "angry",
    frustrated: "angry",
    irritated: "angry",
    hostility: "angry",
    hostile: "angry",
    disgust: "disgusted",
    disgusted: "disgusted",
    revolted: "disgusted",
    appalled: "disgusted",
    contempt: "disgusted",
    contemptuous: "disgusted",
    disdainful: "disgusted",
    fear: "fearful",
    fearful: "fearful",
    afraid: "fearful",
    terrified: "fearful",
    anxious: "fearful",
    nervous: "fearful",
    panic: "fearful",
    panicked: "fearful",
    happiness: "happy",
    happy: "happy",
    joy: "happy",
    joyful: "happy",
    excitement: "happy",
    excited: "happy",
    delight: "happy",
    delighted: "happy",
    neutral: "neutral",
    calm: "neutral",
    composed: "neutral",
    deadpan: "neutral",
    stoic: "neutral",
    sadness: "sad",
    sad: "sad",
    sorrowful: "sad",
    melancholy: "sad",
    melancholic: "sad",
    dejected: "sad",
    surprise: "surprised",
    surprised: "surprised",
    astonished: "surprised",
    shocked: "surprised",
    stunned: "surprised",
  };

  @Input() reply: EmotionProjectReply;
  @Input() index: number;
  @Input() editableDialogue = false;
  @Input() showEmotionLabels = false;
  @Input() isSaving = false;
  @Input() saveFailed = false;
  @Input() showSavedIndicator = false;
  @Input() playbackManagedExternally = false;
  @Input() externalIsPlaying = false;
  @Input() externalIsPlayingOriginal = false;
  @Input() playEnabled = true;
  @Input() isRegeneratingAudio = false;
  @Input() originalPlayEnabled = false;
  @Input() stereoMixUrl: string | null = null;
  @Input() showOriginalAudioButton = false;
  @Input() formatSecondsToSmpte?: (sec: number) => string;
  @Output() saveDialogue = new EventEmitter<string>();
  @Output() playRequested = new EventEmitter<void>();
  @Output() playOriginalRequested = new EventEmitter<void>();
  @ViewChild("dialogueInput")
  private readonly dialogueInputRef?: ElementRef<HTMLTextAreaElement>;

  private currentAudio: HTMLAudioElement | null = null;
  private localSegmentTimeUpdate: (() => void) | null = null;
  private suppressNextBlurSave = false;
  private audioCacheBuster: number | null = null;
  private lastSelectionStart: number | null = null;
  private lastSelectionEnd: number | null = null;
  public isPlaying = false;
  private isPlayingOriginalLocal = false;
  public dialogueDraft = "";
  public activeEmotionLabel: string | null = null;
  public dialogueSegments: Array<{
    text: string;
    emotion: string | null;
    tagClass: string;
  }> = [];

  public get playing(): boolean {
    return this.playbackManagedExternally
      ? this.externalIsPlaying
      : this.isPlaying;
  }

  public get playingOriginal(): boolean {
    return this.playbackManagedExternally
      ? this.externalIsPlayingOriginal
      : this.isPlayingOriginalLocal;
  }

  public get formattedCount(): string {
    const displayIndex = Number(this.index) + 1;
    return displayIndex < 10 ? `0${displayIndex}` : `${displayIndex}`;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.reply) {
      this.dialogueDraft =
        this.reply?.translatedDialogue ?? this.reply?.dialogue ?? "";
      this.activeEmotionLabel = null;
      this.lastSelectionStart = null;
      this.lastSelectionEnd = null;
      this.dialogueSegments = this.buildDialogueSegments();
    }
    if (changes.isRegeneratingAudio) {
      const wasRegenerating =
        changes.isRegeneratingAudio.previousValue === true;
      const isDone = changes.isRegeneratingAudio.currentValue === false;
      if (wasRegenerating && isDone) {
        this.audioCacheBuster = Date.now();
      }
    }
  }

  public onDialogueInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.dialogueDraft = target?.value ?? "";
    this.captureSelection(target);
  }

  public onDialogueSelectionChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.captureSelection(target);
  }

  public get hasDialogueChanges(): boolean {
    return (
      `${this.dialogueDraft}` !==
      `${this.reply?.translatedDialogue ?? this.reply?.dialogue ?? ""}`
    );
  }

  public get emotionLabels(): string[] {
    const raw = `${this.reply?.dominantEmotion ?? ""}`.trim();
    if (!raw) {
      return [];
    }

    return raw
      .split(/[,\|]/g)
      .map((label) => label.replace(/[[\]"']/g, "").trim())
      .filter((label) => !!label);
  }

  public get sentenceEmotionChips(): Array<{ text: string; emotion: string }> {
    const sentences = this.reply?.sentenceEmotions ?? [];
    return sentences
      .filter((sentence) => !!`${sentence?.emotion ?? ""}`.trim())
      .map((sentence) => ({
        text: `${sentence?.text ?? ""}`.trim(),
        emotion: `${sentence?.emotion ?? ""}`.trim(),
      }));
  }

  public get allEmotionChips(): string[] {
    const allKeys = Object.keys(EmotionReplyComponent.EMOTION_SUGGESTION_MAP);
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const chip of this.sentenceEmotionChips) {
      const key = this.normalizeEmotionKey(chip.emotion);
      if (key && allKeys.includes(key) && !seen.has(key)) {
        seen.add(key);
        ordered.push(key);
      }
    }
    for (const key of allKeys) {
      if (!seen.has(key)) {
        ordered.push(key);
      }
    }
    return ordered;
  }

  public get hasInlineEmotions(): boolean {
    return this.dialogueSegments.some((segment) => !!segment.emotion);
  }

  private buildDialogueSegments(): Array<{
    text: string;
    emotion: string | null;
    tagClass: string;
  }> {
    const source = `${this.reply?.dialogue ?? ""}`;
    const sentences = this.sentenceEmotionChips;

    if (!source || !sentences.length) {
      return [{ text: source, emotion: null, tagClass: "" }];
    }

    const sourceTokens = this.tokenizeWithOffsets(source);
    if (!sourceTokens.length) {
      return [{ text: source, emotion: null, tagClass: "" }];
    }
    const starts: Array<{ tokenIdx: number; emotion: string }> = [];
    let searchFrom = 0;
    for (const sentence of sentences) {
      const sentenceWords = this.normalizeWords(sentence.text);
      if (!sentenceWords.length) {
        continue;
      }
      const tokenIdx = this.findSentenceStart(
        sourceTokens,
        sentenceWords,
        searchFrom
      );
      if (tokenIdx < 0) {
        continue;
      }
      starts.push({ tokenIdx, emotion: sentence.emotion });
      searchFrom = tokenIdx + 1;
    }

    const ranges: Array<{ start: number; end: number; emotion: string }> =
      starts.map((entry, index) => {
        const endTokenIdx =
          index + 1 < starts.length
            ? starts[index + 1].tokenIdx - 1
            : sourceTokens.length - 1;
        const safeEndIdx = Math.max(entry.tokenIdx, endTokenIdx);
        return {
          start: sourceTokens[entry.tokenIdx].start,
          end: sourceTokens[safeEndIdx].end,
          emotion: entry.emotion,
        };
      });

    if (!ranges.length) {
      return [{ text: source, emotion: null, tagClass: "" }];
    }

    const segments: Array<{
      text: string;
      emotion: string | null;
      tagClass: string;
    }> = [];
    let cursor = 0;
    for (const range of ranges) {
      if (range.start > cursor) {
        segments.push({
          text: source.slice(cursor, range.start),
          emotion: null,
          tagClass: "",
        });
      }
      segments.push({
        text: source.slice(range.start, range.end),
        emotion: range.emotion,
        tagClass: this.getEmotionTagClass(range.emotion),
      });
      cursor = range.end;
    }
    if (cursor < source.length) {
      segments.push({
        text: source.slice(cursor),
        emotion: null,
        tagClass: "",
      });
    }

    return segments;
  }

  private findSentenceStart(
    tokens: Array<{ word: string; start: number; end: number }>,
    words: string[],
    from: number
  ): number {
    let best = -1;
    let bestScore = -1;
    const probes = Math.min(3, words.length);
    for (let p = 0; p < probes; p++) {
      for (let i = from; i < tokens.length; i++) {
        if (tokens[i].word !== words[p]) {
          continue;
        }
        let score = 0;
        let cursor = i + 1;
        let wordIdx = p + 1;
        let look = 0;
        while (cursor < tokens.length && wordIdx < words.length && look < 8) {
          if (tokens[cursor].word === words[wordIdx]) {
            score++;
            wordIdx++;
          }
          cursor++;
          look++;
        }
        const anchor = Math.max(from, i - p);
        if (score > bestScore) {
          bestScore = score;
          best = anchor;
        }
        break;
      }
    }
    return best;
  }

  private tokenizeWithOffsets(
    source: string
  ): Array<{ word: string; start: number; end: number }> {
    const tokens: Array<{ word: string; start: number; end: number }> = [];
    const regex = /\S+/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(source)) !== null) {
      const normalized = this.normalizeWord(match[0]);
      if (!normalized) {
        continue;
      }
      tokens.push({
        word: normalized,
        start: match.index,
        end: match.index + match[0].length,
      });
    }
    return tokens;
  }

  private normalizeWords(text: string): string[] {
    return `${text ?? ""}`
      .split(/\s+/g)
      .map((word) => this.normalizeWord(word))
      .filter((word) => !!word);
  }

  private normalizeWord(word: string): string {
    return `${word ?? ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  public get emotionSuggestions(): string[] {
    const key = this.normalizeEmotionKey(this.activeEmotionLabel);
    if (!key) {
      return [];
    }
    return EmotionReplyComponent.EMOTION_SUGGESTION_MAP[key] ?? [];
  }

  public getEmotionTagClass(label: string): string {
    const normalized = `${label || ""}`.trim().toLowerCase();

    if (
      ["happy", "joy", "excited", "love", "optimistic"].includes(normalized)
    ) {
      return "tag-deliver";
    }
    if (["sad", "grief", "melancholy"].includes(normalized)) {
      return "tag-share";
    }
    if (["angry", "anger", "frustrated", "furious"].includes(normalized)) {
      return "tag-publish";
    }
    if (["fear", "scared", "anxious", "nervous"].includes(normalized)) {
      return "tag-archive";
    }
    if (["surprised", "surprise"].includes(normalized)) {
      return "tag-find";
    }
    if (["disgust", "disgusted"].includes(normalized)) {
      return "tag-admin";
    }
    if (["neutral", "calm", "regular"].includes(normalized)) {
      return "tag-regular";
    }

    return "tag-find";
  }

  public saveDraft(): void {
    if (!this.hasDialogueChanges || this.isSaving) {
      return;
    }
    this.saveDialogue.emit(this.dialogueDraft);
  }

  public onEmotionTagMouseDown(): void {
    this.suppressNextBlurSave = true;
  }

  public onEmotionLabelClick(label: string): void {
    if (!this.editableDialogue) {
      return;
    }

    this.activeEmotionLabel = this.activeEmotionLabel === label ? null : label;
  }

  public onEmotionSuggestionClick(suggestion: string): void {
    if (!this.editableDialogue) {
      return;
    }

    this.dialogueDraft = this.insertSuggestionAtSelection(
      this.dialogueDraft,
      suggestion
    );
    this.saveDraft();
  }

  public onDialogueBlur(): void {
    if (!this.editableDialogue) {
      return;
    }
    if (this.suppressNextBlurSave) {
      this.suppressNextBlurSave = false;
      return;
    }
    this.saveDraft();
  }

  public onDialogueKeydown(event: KeyboardEvent): void {
    if (!this.editableDialogue || event.key !== "Enter" || event.shiftKey) {
      return;
    }
    event.preventDefault();
    this.suppressNextBlurSave = true;
    this.saveDraft();
  }

  public togglePlay(): void {
    if (this.playbackManagedExternally) {
      if (!this.playEnabled) {
        return;
      }
      this.playRequested.emit();
      return;
    }

    if (!this.reply?.audioFilePath) {
      return;
    }

    if (this.isPlaying && this.currentAudio) {
      this.stopLocalPlayback();
      return;
    }

    this.stopLocalPlayback();
    const audioUrl = this.audioCacheBuster
      ? `${this.reply.audioFilePath}?t=${this.audioCacheBuster}`
      : this.reply.audioFilePath;
    this.audioCacheBuster = null;
    this.currentAudio = new Audio(audioUrl);
    this.isPlaying = true;
    this.currentAudio.addEventListener("ended", () => this.stopLocalPlayback());
    this.currentAudio.addEventListener("error", () => this.stopLocalPlayback());
    this.currentAudio.play().catch(() => this.stopLocalPlayback());
  }

  public togglePlayOriginal(): void {
    if (this.playbackManagedExternally) {
      if (!this.originalPlayEnabled) {
        return;
      }
      this.playOriginalRequested.emit();
      return;
    }

    const stereo = `${this.stereoMixUrl ?? ""}`.trim();
    if (!stereo || !this.reply) {
      return;
    }

    const start = Number(this.reply.startSec) || 0;
    const end = Number(this.reply.endSec) || 0;
    if (end <= start + 0.01) {
      return;
    }

    if (this.isPlayingOriginalLocal && this.currentAudio) {
      this.stopLocalPlayback();
      return;
    }

    this.stopLocalPlayback();
    const audio = new Audio(stereo);
    this.currentAudio = audio;
    this.isPlayingOriginalLocal = true;

    this.localSegmentTimeUpdate = () => {
      if (audio.currentTime >= end - 0.02) {
        audio.pause();
        this.stopLocalPlayback();
      }
    };

    const startPlayback = () => {
      try {
        audio.currentTime = start;
      } catch {
        // ignore seek errors
      }
      if (this.localSegmentTimeUpdate) {
        audio.addEventListener("timeupdate", this.localSegmentTimeUpdate);
      }
      audio.play().catch(() => this.stopLocalPlayback());
    };

    audio.addEventListener("ended", () => this.stopLocalPlayback());
    audio.addEventListener("error", () => this.stopLocalPlayback());

    if (audio.readyState >= 1) {
      startPlayback();
    } else {
      audio.addEventListener("loadeddata", startPlayback, { once: true });
    }
  }

  private stopLocalPlayback(): void {
    if (this.currentAudio && this.localSegmentTimeUpdate) {
      this.currentAudio.removeEventListener(
        "timeupdate",
        this.localSegmentTimeUpdate
      );
    }
    this.localSegmentTimeUpdate = null;
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    this.isPlaying = false;
    this.isPlayingOriginalLocal = false;
  }

  private normalizeEmotionKey(label: string | null): string {
    const normalized = `${label || ""}`.trim().toLowerCase();
    if (!normalized) {
      return "";
    }
    return (
      EmotionReplyComponent.EMOTION_CANONICAL_MAP[normalized] ?? normalized
    );
  }

  private captureSelection(target: HTMLTextAreaElement | null): void {
    if (!target) {
      return;
    }
    this.lastSelectionStart = Number.isInteger(target.selectionStart)
      ? target.selectionStart
      : null;
    this.lastSelectionEnd = Number.isInteger(target.selectionEnd)
      ? target.selectionEnd
      : null;
  }

  private insertSuggestionAtSelection(
    dialogue: string,
    suggestion: string
  ): string {
    const currentDialogue = `${dialogue ?? ""}`;
    const cue = `${suggestion ?? ""}`.trim();

    if (!cue) {
      return currentDialogue;
    }
    if (!currentDialogue) {
      this.lastSelectionStart = cue.length;
      this.lastSelectionEnd = cue.length;
      return cue;
    }

    const canUseSelection =
      this.lastSelectionStart !== null &&
      this.lastSelectionEnd !== null &&
      this.lastSelectionStart >= 0 &&
      this.lastSelectionEnd >= this.lastSelectionStart &&
      this.lastSelectionEnd <= currentDialogue.length;

    if (canUseSelection) {
      const selectionStart = this.lastSelectionStart as number;
      const selectionEnd = this.lastSelectionEnd as number;
      const updated =
        currentDialogue.slice(0, selectionStart) +
        cue +
        currentDialogue.slice(selectionEnd);
      const nextCaret = selectionStart + cue.length;
      this.lastSelectionStart = nextCaret;
      this.lastSelectionEnd = nextCaret;
      this.restoreCaret(nextCaret);
      return updated;
    }

    const updated = `${cue} ${currentDialogue}`.trim();
    this.lastSelectionStart = cue.length;
    this.lastSelectionEnd = cue.length;
    return updated;
  }

  private restoreCaret(position: number): void {
    if (!this.dialogueInputRef?.nativeElement) {
      return;
    }
    setTimeout(() => {
      const textarea = this.dialogueInputRef?.nativeElement;
      if (!textarea) {
        return;
      }
      textarea.focus();
      textarea.setSelectionRange(position, position);
    });
  }
}
